import React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Button, Col, Form, FormInstance, Image, Input, PageTitle, Row, Table, Tooltip, Typography } from 'components';
import { FilterIcon, ViewIcon } from 'components/common/icons';
import { Filters } from './filters';
import { useCountries } from './useCountries';
import styles from './styles.module.scss';

const { Search } = Input;

type IdAndName = {
  name: string;
  id: string;
};

export interface CountryDataType {
  id: string;
  uuid: string | number;
  commonName: string;
  regions: IdAndName[];
  languages: IdAndName[];
  subRegions: string[];
  lastModifiedDate: string;
}

export const Countries: React.FC = () => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    applyFilters,
    resetFilters,
  } = useCountries();

  const columns: ColumnsType<CountryDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Country',
      key: 'commonName',
      sorter: true,
      render: (i) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {i?.flagLocation && (
              <Image
                src={i?.flagLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{i?.commonName}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Region',
      dataIndex: 'regions',
      key: 'regions',
      render: (value) => {
        if (value?.length) {
          return (
            <Row align={'middle'} justify={'space-between'}>
              <Typography.Text>{value[0]}</Typography.Text>
              <Tooltip title={value.join(', ')}>
                {value?.length > 1 ? <Typography.Text underline>+{value?.length - 1}</Typography.Text> : ''}
              </Tooltip>
            </Row>
          );
        }
        return '';
      },
      minWidth: 120,
      sorter: false,
    },
    {
      title: 'Sub-Region',
      dataIndex: 'subRegions',
      key: 'subRegions',
      render: (value) => {
        if (value?.length) {
          return (
            <Row align={'middle'} justify={'space-between'}>
              <Typography.Text>{value[0]}</Typography.Text>
              <Tooltip title={value.join(', ')}>
                {value?.length > 1 ? <Typography.Text underline>+{value?.length - 1}</Typography.Text> : ''}
              </Tooltip>
            </Row>
          );
        }
        return '';
      },
      minWidth: 120,
      sorter: false,
    },
    {
      title: 'Language',
      dataIndex: 'languages',
      key: 'languages',
      render: (value) => {
        if (value?.length) {
          return (
            <Row align={'middle'} justify={'space-between'}>
              <Typography.Text>{value[0]}</Typography.Text>
              <Tooltip title={value.join(', ')}>
                {value?.length > 1 ? <Typography.Text underline>+{value?.length - 1}</Typography.Text> : ''}
              </Tooltip>
            </Row>
          );
        }
        return '';
      },
      minWidth: 120,
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      render: (value) => value?.day,
      minWidth: 150,
      sorter: true,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Countries'} />
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <Table
                tableLayout={'auto'}
                bordered
                scroll={{
                  x: true,
                }}
                rowKey="id"
                size={'small'}
                dataSource={data}
                columns={columns}
                loading={{
                  indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                  spinning: isLoading,
                }}
                locale={
                  isLoading
                    ? { emptyText: null } // Hide "No Data" text while loading
                    : {} // Use default AntD "No Data" icon when empty
                }
                onChange={handleTableChange}
                pagination={{
                  position: ['bottomCenter'],
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} items`,
                  size: 'default',
                  current: pagination.page,
                  pageSize: pagination.size,
                  total: total,
                }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
    </Col>
  );
};
